"use client";

import { motion } from "framer-motion";
import React, { FC, MouseEvent, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import useMediaQuery from "@/hooks/useMediaQuery";
import useScroll from "@/hooks/useScroll";
import { UnitAnchorsProps } from "@/types";

const UnitAnchors: FC<UnitAnchorsProps> = ({ anchors }) => {
  const [currentSelect, setCurrentSelect] = useState<number>();
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const headerDesktop = document?.querySelector("#header-desktop");
  const headerMobile = document?.querySelector("#header-mobile");
  const [headerMobileOffset, setHeaderMobileOffset] = useState<number>(
    isDesktop ? headerDesktop?.getBoundingClientRect().height || 100 : (headerMobile as HTMLElement)?.offsetTop | 0,
  );
  const [transition, setTransition] = useState<{}>({
    duration: 0.15,
    delay: 0.325,
  });
  const scroll = useScroll();
  const headerHeight = headerDesktop?.getBoundingClientRect().height;
  const mobileHeaderHeight = headerMobile?.getBoundingClientRect().height;

  const [isSticky, setIsSticky] = useState<boolean>(false);

  const handleScroll = () => {
    let anchorComponent = document.querySelector("#anchor-component");
    if (isDesktop) {
      if (anchorComponent && headerHeight) {
        setIsSticky(anchorComponent.getBoundingClientRect().top <= headerHeight);
      }
    } else {
      if (anchorComponent && mobileHeaderHeight) {
        setIsSticky(anchorComponent.getBoundingClientRect().top <= mobileHeaderHeight);
      }
    }
  };

  window.addEventListener("scroll", handleScroll);

  useEffect(() => {
    if (!isDesktop) {
      if (scroll > 0) {
        setTransition({
          duration: 0.05,
        });
        setHeaderMobileOffset(0);
      } else if (scroll < 0) {
        setTransition({
          duration: 0.15,
          delay: 0.325,
        });
        setHeaderMobileOffset(headerMobile?.getBoundingClientRect().height || 63);
        if (isSticky) {
          headerMobile?.classList.remove("shadow-strong");
        }
      }
    }
  }, [scroll]);

  useEffect(() => {
    if (isSticky) {
      headerDesktop?.classList.remove("shadow-strong");
    } else {
      headerDesktop?.classList.add("shadow-strong");
      headerMobile?.classList.add("shadow-strong");
    }
  }, [isSticky]);

  const variants = {
    open: { top: (headerMobile?.getBoundingClientRect().height || 63) + "px" },
    close: { top: isDesktop ? (headerDesktop?.getBoundingClientRect().height || 100) + "px" : "0px" },
  };

  return (
    <motion.div
      className={`sticky bg-white z-10`}
      id="anchor-component"
      transition={transition}
      variants={variants}
      animate={headerMobileOffset ? "open" : "close"}
    >
      <div className={`px-auto text-center whitespace-nowrap overflow-x-auto ${isSticky ? "shadow-strong" : ""}`}>
        {anchors.map((anchor, i) => {
          let componentID: string = anchor.component;
          switch (anchor.component) {
            case "unit-list":
              componentID = "AUL-component";
              break;
            case "project":
              componentID = "editorial-1-col";
              break;
            default:
              componentID = anchor.component;
              break;
          }
          return (
            <a
              href={`#${componentID}`}
              onClick={(event: MouseEvent<HTMLAnchorElement>) => {
                event.preventDefault();

                let target = document.querySelector(`#${componentID}`);

                if (target) {
                  let targetPosition =
                    target.getBoundingClientRect().top -
                    ((isDesktop ? headerDesktop?.getBoundingClientRect().height : headerMobile?.getBoundingClientRect().height) || 100) -
                    (document.querySelector("#anchor-component")?.getBoundingClientRect().height || 57);
                  window.scrollTo({
                    top: targetPosition + window.scrollY,
                    behavior: "smooth",
                  });
                }

                setCurrentSelect(i);
              }}
              className={`inline-block p-m font-secondary ${i === currentSelect ? "text-black border-b-black" : "text-gray-30 border-b-gray-30"} border-b hover:text-black hover:border-b-black`}
              key={`unit-anchor-${i}`}
            >
              {anchor.label}
            </a>
          );
        })}
      </div>
    </motion.div>
  );
};
export default UnitAnchors;
